/**
 * Filter Rule Editor
 *
 * Comprehensive editor for all filter modes:
 * - Exact: Multi-select from available values
 * - Pattern: Regex or wildcard matching (X for digits)
 * - Comparison: Numeric comparisons (>, <, etc.)
 * - Ranking: Top/bottom N features
 */

import { useState, useEffect } from "react";
import { Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { FilterRule } from "@/stores/useStudioStore";

interface FilterRuleEditorProps {
  filter: FilterRule;
  availableValues: string[]; // Unique values for this property (for exact mode)
  onUpdate: (updates: Partial<FilterRule>) => void;
  onRemove: () => void;
}

export function FilterRuleEditor({
  filter,
  availableValues,
  onUpdate,
  onRemove,
}: FilterRuleEditorProps) {
  const [patternDraft, setPatternDraft] = useState("");
  const [rankingCountDraft, setRankingCountDraft] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Update draft when filter changes
  useEffect(() => {
    if (filter.mode === "ranking" && filter.rankingCount !== undefined) {
      setRankingCountDraft(String(filter.rankingCount));
    }
  }, [filter.mode, filter.rankingCount]);

  const handleAddPattern = () => {
    const trimmed = patternDraft.trim();
    if (!trimmed || filter.values.includes(trimmed)) {
      setPatternDraft("");
      return;
    }
    onUpdate({ values: [...filter.values, trimmed] });
    setPatternDraft("");
  };

  const handleRemovePattern = (value: string) => {
    onUpdate({ values: filter.values.filter((v) => v !== value) });
  };

  const handleToggleValue = (value: string, checked: boolean) => {
    if (checked) {
      onUpdate({ values: [...filter.values, value] });
    } else {
      onUpdate({ values: filter.values.filter((v) => v !== value) });
    }
  };

  const helperText =
    filter.mode === "pattern"
      ? "Use X as a digit wildcard (e.g. X1X) or wrap custom regex in /slashes/."
      : filter.mode === "comparison"
        ? "Compare property values using the selected operator."
        : filter.mode === "ranking"
          ? "Keep only the top or bottom N features by this property."
          : "Select the exact values that should remain clickable.";

  // Filter available values by search query
  const filteredValues =
    searchQuery.trim() === ""
      ? availableValues
      : availableValues.filter((v) =>
          v.toLowerCase().includes(searchQuery.toLowerCase())
        );

  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="space-y-1">
          <p className="text-sm font-semibold">{filter.propertyKey}</p>
          <p className="text-xs text-muted-foreground">{helperText}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={filter.mode}
            onValueChange={(value) => {
              const newMode = value as FilterRule["mode"];
              // Set appropriate defaults for each mode
              if (newMode === "comparison") {
                onUpdate({
                  mode: newMode,
                  values: [],
                  operator: ">",
                  rankingDirection: undefined,
                  rankingCount: undefined,
                });
              } else if (newMode === "ranking") {
                onUpdate({
                  mode: newMode,
                  values: [],
                  operator: undefined,
                  rankingDirection: "top",
                  rankingCount: 10,
                });
              } else {
                onUpdate({
                  mode: newMode,
                  values: [],
                  operator: undefined,
                  rankingDirection: undefined,
                  rankingCount: undefined,
                });
              }
            }}
          >
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="exact">Exact</SelectItem>
              <SelectItem value="comparison">Comparison</SelectItem>
              <SelectItem value="ranking">Ranking</SelectItem>
              <SelectItem value="pattern">Pattern</SelectItem>
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Comparison Mode */}
      {filter.mode === "comparison" && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Input
              value={filter.values[0] ?? ""}
              onChange={(e) => onUpdate({ values: [e.target.value] })}
              placeholder="Enter value to compare (e.g. 1000000)"
            />
          </div>
          <Select
            value={filter.operator ?? ">"}
            onValueChange={(value) =>
              onUpdate({ operator: value as FilterRule["operator"] })
            }
          >
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value=">">Greater than</SelectItem>
              <SelectItem value="<">Less than</SelectItem>
              <SelectItem value=">=">Greater or equal</SelectItem>
              <SelectItem value="<=">Less or equal</SelectItem>
              <SelectItem value="=">Equal to</SelectItem>
              <SelectItem value="!=">Not equal to</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Ranking Mode */}
      {filter.mode === "ranking" && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <Select
            value={filter.rankingDirection ?? "top"}
            onValueChange={(value) =>
              onUpdate({ rankingDirection: value as "top" | "bottom" })
            }
          >
            <SelectTrigger className="w-full sm:w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="top">Top</SelectItem>
              <SelectItem value="bottom">Bottom</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex-1">
            <Input
              type="number"
              min="1"
              value={rankingCountDraft || filter.rankingCount || 10}
              onChange={(e) => {
                const value = e.target.value;
                setRankingCountDraft(value);
                const parsed = parseInt(value, 10);
                if (!isNaN(parsed) && parsed >= 1) {
                  onUpdate({ rankingCount: parsed });
                }
              }}
              onBlur={() => {
                const parsed = parseInt(rankingCountDraft, 10);
                if (isNaN(parsed) || parsed < 1) {
                  setRankingCountDraft("10");
                  onUpdate({ rankingCount: 10 });
                }
              }}
              placeholder="Count (e.g. 10)"
            />
          </div>
        </div>
      )}

      {/* Exact Mode */}
      {filter.mode === "exact" && (
        <div className="space-y-3">
          {availableValues.length > 0 ? (
            <>
              {/* Search input */}
              {availableValues.length > 10 && (
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search values..."
                  className="mb-2"
                />
              )}

              {/* Scrollable list of checkboxes */}
              <div className="max-h-64 overflow-y-auto rounded-md border p-3 space-y-2">
                {filteredValues.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-2">
                    No values found
                  </p>
                ) : (
                  filteredValues.map((value) => {
                    const checked = filter.values.includes(value);
                    return (
                      <div
                        key={value}
                        className="flex items-center gap-3 hover:bg-accent/50 rounded p-2 cursor-pointer"
                        onClick={() => handleToggleValue(value, !checked)}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(state) =>
                            handleToggleValue(value, Boolean(state))
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Label className="text-sm cursor-pointer flex-1">
                          {value}
                        </Label>
                      </div>
                    );
                  })
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {filter.values.length} of {availableValues.length} selected
              </p>
            </>
          ) : (
            <p className="text-xs text-muted-foreground border border-dashed rounded p-3">
              No discrete values detected for this property.
            </p>
          )}
        </div>
      )}

      {/* Pattern Mode */}
      {filter.mode === "pattern" && (
        <div className="space-y-3">
          {filter.values.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {filter.values.map((value) => (
                <span
                  key={value}
                  className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                >
                  {value}
                  <button
                    type="button"
                    className="rounded-full hover:bg-primary/20 p-0.5"
                    onClick={() => handleRemovePattern(value)}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          {filter.values.length === 0 && (
            <p className="text-xs text-muted-foreground">
              Add at least one pattern to keep matching values.
            </p>
          )}
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              value={patternDraft}
              onChange={(e) => setPatternDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddPattern();
                }
              }}
              placeholder="Enter pattern (e.g. X1X or /^(404|470)$/)"
              className="flex-1"
            />
            <Button
              type="button"
              onClick={handleAddPattern}
              disabled={!patternDraft.trim()}
            >
              Add pattern
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
